import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Box,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { appointmentService, authService } from '../../services/api';
import TopBar from '../../components/TopBar';

const OPDPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [appointments, setAppointments] = useState([]);

  useEffect(() => {
    loadTodayAppointments();
  }, []);

  const loadTodayAppointments = async () => {
    try {
      const response = await appointmentService.getTodayAppointments();
      const opdAppointments = response.data.filter(apt =>
        apt.type === 'OPD' &&
        apt.status !== 'Completed' &&
        (user?.role !== 'Doctor' || apt.doctorId === user?.id)
      );
      const sortedAppointments = opdAppointments.sort((a, b) => {
        const timeA = a.appointmentTime || '00:00';
        const timeB = b.appointmentTime || '00:00';
        return timeA.localeCompare(timeB);
      });
      setAppointments(sortedAppointments);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const handleSelectPatient = (appointment) => {
    navigate(`/patient/${appointment.patientId}/${appointment.id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="OPD - Outpatient Department" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Today's OPD Appointments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {appointments.length} patient{appointments.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {appointments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No appointments scheduled for today
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={1.5}>
              {appointments.map((appointment, index) => (
                <Grid item xs={6} sm={4} md={2} key={appointment.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      transition: 'all 0.18s',
                      '&:hover': {
                        boxShadow: '0 4px 14px rgba(33,150,243,0.25)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => handleSelectPatient(appointment)}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75 }}>
                        <Box
                          sx={{
                            bgcolor: '#2196f3',
                            color: '#fff',
                            borderRadius: '50%',
                            minWidth: 26,
                            height: 26,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            mr: 0.75,
                            flexShrink: 0,
                            mt: 0.1,
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.3,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            wordBreak: 'break-word',
                          }}
                        >
                          {appointment.patient?.firstName} {appointment.patient?.lastName}
                        </Typography>
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                        {appointment.appointmentTime || '—'} &nbsp;·&nbsp;
                        {appointment.patient?.gender?.[0] || '?'}/{appointment.patient?.age ?? '?'}
                      </Typography>

                      {appointment.notes && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            mt: 0.4,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {appointment.notes}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default OPDPage;
